import { useState, useEffect } from 'react';
import styled from 'styled-components';
import Breadcrumbs from '../../components/Breadcrumbs';
import '../../styles/PacDebt.css';
import Footer from '../../components/Footer';

const BulkUpdateTool = () => {

  // useEffect(() => {
  //   Swal.fire({
  //       icon: "warning",
  //       title: "Reminder to all users",
  //       text: "Please only use live data when interacting with this page.",
  //       confirmButtonColor: '#A5CC2D', 
  //     });
  // }, []);
  
  const [activeTab, setActiveTab] = useState('upload');
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState({ message: '', isSuccess: false, show: false });
  const [historyStatus, setHistoryStatus] = useState({ message: '', isSuccess: false, show: false });
  const [responseData, setResponseData] = useState(null);
  const [uploadedCsvData, setUploadedCsvData] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [debtIdFilter, setDebtIdFilter] = useState('');
  const [resultFilter, setResultFilter] = useState('All');
  const [legalNegotiatorFilter, setLegalNegotiatorFilter] = useState('All');
  const [legalResolutionStatusFilter, setLegalResolutionStatusFilter] = useState('All');
  const [poaTypeFilter, setPoaTypeFilter] = useState('All');
  
  // Enhanced validation and editing states
  const [validationErrors, setValidationErrors] = useState({});
  const [editedData, setEditedData] = useState({});
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // ─── Contact Update tab state (MVP: upload only) ─────────────────────────
  // Lives alongside Upload/History but is structurally independent: contact-
  // level fields keyed by contact_id (not debt_id), its own validation rules,
  // its own endpoint, its own preview state. History view is intentionally
  // out of scope for the MVP — to add later, reintroduce contactHistoryData,
  // contactStartDate/EndDate, contactHistoryStatus, plus a sub-tab toggle.
  const [contactFile, setContactFile] = useState(null);
  const [contactStatus, setContactStatus] = useState({ message: '', isSuccess: false, show: false });
  const [contactResponseData, setContactResponseData] = useState(null);
  const [contactUploadedCsvData, setContactUploadedCsvData] = useState(null);
  const [contactValidationErrors, setContactValidationErrors] = useState({});
  const [contactEditedData, setContactEditedData] = useState({});
  const [contactIsPreviewMode, setContactIsPreviewMode] = useState(false);
  const [contactCurrentPage, setContactCurrentPage] = useState(1);
  const [contactItemsPerPage, setContactItemsPerPage] = useState(25);
  const [contactSortConfig, setContactSortConfig] = useState({ key: null, direction: 'asc' });

  // ─── Historical Logs tab — type selector ─────────────────────────────────
  // Lets users switch the history view between Debt Level Update events and
  // Contact Level Update events. Each type fetches from its own Pipedream
  // workflow and renders with its own column set. Flipping the toggle clears
  // historyData (handled in a useEffect below) so users don't see stale rows
  // shaped for the other schema.
  const [historyType, setHistoryType] = useState('debt');

  // Column order state
  const [uploadColumnOrder, setUploadColumnOrder] = useState([
    'debt_id', 'has_summons', 'poa_sent_date', 'summons_date', 'service_date',
    'legal_negotiator', 'legal_resolution_status', 'poa_type', 'prelegal',
    'sif_auth', 'sif_letter',
    'void_reason', 'voided_date', 'voided_sif_id', 'voided_sif_remaining_client_obligation',
    'notes'
  ]);

  // Loan / contact-level fields are now their own tab (keyed by contact_id,
  // not debt_id). See contactColumnOrder below + the Contact Update tab.
  
  const [historyColumnOrder, setHistoryColumnOrder] = useState([
    'debt_id', 'has_summons', 'poa_sent_date', 'summons_date', 'service_date',
    'legal_negotiator', 'legal_resolution_status', 'poa_type', 'prelegal',
    'void_reason', 'voided_date', 'voided_sif_id', 'voided_sif_remaining_client_obligation',
    'date', 'time', 'success'
  ]);
  
  const [draggedColumn, setDraggedColumn] = useState(null);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debtIdFilter, resultFilter, legalNegotiatorFilter, legalResolutionStatusFilter, poaTypeFilter]);

  // Set default dates
  useEffect(() => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    const lastMonthStr = lastMonth.toISOString().split('T')[0];
    setStartDate(lastMonthStr);
    setEndDate(todayStr);
  }, []);

  // Column definitions
  const uploadColumnDefinitions = {
    debt_id: { name: 'Debt ID', width: '80px' },
    has_summons: { name: 'Has Summons', width: '100px' },
    poa_sent_date: { name: 'POA Sent Date', width: '120px' },
    summons_date: { name: 'Summons Date', width: '120px' },
    service_date: { name: 'Service Date', width: '120px' },
    legal_negotiator: { name: 'Legal Negotiator', width: '130px' },
    legal_resolution_status: { name: 'Legal Resolution Status', width: '150px' },
    poa_type: { name: 'POA Type', width: '100px' },
    prelegal: { name: 'Prelegal', width: '100px' },
    sif_auth: { name: 'SIF Auth', width: '100px' },
    sif_letter: { name: 'SIF Letter', width: '100px' },
    void_reason: { name: 'Void Reason', width: '150px' },
    voided_date: { name: 'Voided Date', width: '120px' },
    voided_sif_id: { name: 'Voided SIF ID', width: '120px' },
    voided_sif_remaining_client_obligation: { name: 'Voided SIF Remaining Client Obligation', width: '220px' },
    notes: { name: 'Notes', width: '150px' }
  };

  const historyColumnDefinitions = {
    ...uploadColumnDefinitions,
    date: { name: 'Date', width: '100px' },
    time: { name: 'Time', width: '80px' },
    success: { name: 'Result', width: '80px' }
  };

  // ─── Contact Update tab column registry ──────────────────────────────────
  // Contact-level fields keyed by contact_id. Held as state so the drag-to-
  // reorder handler can mutate it, same pattern as uploadColumnOrder /
  // historyColumnOrder.
  const [contactColumnOrder, setContactColumnOrder] = useState([
    'contact_id',
    'loan_funded_date', 'loan_funded_amount', 'loan_partner', 'loan_outreach_status', 'pdr_loan_status',
    'last_contact_date', 'last_check_in_attempt', 'last_check_in_completed',
  ]);

  const contactColumnDefinitions = {
    contact_id:              { name: 'Contact ID',              width: '100px' },
    loan_funded_date:        { name: 'Loan Funded Date',        width: '130px' },
    loan_funded_amount:      { name: 'Loan Funded Amount',      width: '140px' },
    loan_partner:            { name: 'Loan Partner',            width: '120px' },
    loan_outreach_status:    { name: 'Loan Outreach Status',    width: '150px' },
    pdr_loan_status:         { name: 'PDR Loan Status',         width: '130px' },
    last_contact_date:       { name: 'Last Contact Date',       width: '130px' },
    last_check_in_attempt:   { name: 'Last Check In Attempt',   width: '150px' },
    last_check_in_completed: { name: 'Last Check In Completed', width: '160px' },
  };

  // Column order for the Historical Logs tab when viewing CONTACT updates.
  // Same shape as the contact upload columns, with date/time/success tacked
  // on at the end — mirrors how historyColumnOrder extends uploadColumnOrder.
  const contactHistoryColumnOrder = [
    ...contactColumnOrder,
    'date', 'time', 'success',
  ];

  const contactHistoryColumnDefinitions = {
    ...contactColumnDefinitions,
    date:    { name: 'Date',   width: '100px' },
    time:    { name: 'Time',   width: '80px' },
    success: { name: 'Result', width: '80px' },
  };

  // CSV header normalizer for the Contact tab. Mirrors getStandardFieldName
  // (lowercase + replace non-alphanumeric with underscore, then map). Kept
  // separate so a CSV column named e.g. "debt_id" coming into the Contact
  // tab does not silently map to anything — only contact-level keys are
  // recognized; unknown headers fall through unchanged.
  const getContactStandardFieldName = (fieldName) => {
    const field = fieldName.toLowerCase().replace(/[^a-z0-9]/g, '_');
    const fieldMap = {
      'contact_id':              'contact_id',
      'loan_funded_date':        'loan_funded_date',
      'loan_funded_amount':      'loan_funded_amount',
      'loan_partner':            'loan_partner',
      'loan_outreach_status':    'loan_outreach_status',
      'pdr_loan_status':         'pdr_loan_status',
      'last_contact_date':       'last_contact_date',
      'last_check_in_attempt':   'last_check_in_attempt',
      'last_check_in_completed': 'last_check_in_completed',
    };
    return fieldMap[field] || fieldName;
  };

  // Drag handlers
  const handleDragStart = (e, columnKey) => {
    setDraggedColumn(columnKey);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetColumnKey, isUploadTable = false) => {
    e.preventDefault();
    
    if (!draggedColumn || draggedColumn === targetColumnKey) {
      setDraggedColumn(null);
      return;
    }

    if (isUploadTable) {
      const newColumnOrder = [...uploadColumnOrder];
      const draggedIndex = newColumnOrder.indexOf(draggedColumn);
      const targetIndex = newColumnOrder.indexOf(targetColumnKey);

      newColumnOrder.splice(draggedIndex, 1);
      newColumnOrder.splice(targetIndex, 0, draggedColumn);
      
      setUploadColumnOrder(newColumnOrder);
    } else {
      const newColumnOrder = [...historyColumnOrder];
      const draggedIndex = newColumnOrder.indexOf(draggedColumn);
      const targetIndex = newColumnOrder.indexOf(targetColumnKey);

      newColumnOrder.splice(draggedIndex, 1);
      newColumnOrder.splice(targetIndex, 0, draggedColumn);
      
      setHistoryColumnOrder(newColumnOrder);
    }
    
    setDraggedColumn(null);
  };

  // Sorting handlers
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return ' ↕️';
    }
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const getPageNumbers = (totalPages) => {
    const pageNumbers = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
      const end = Math.min(totalPages, start + maxVisible - 1);
      
      if (start > 1) {
        pageNumbers.push(1);
        if (start > 2) pageNumbers.push('...');
      }
      
      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }
      
      if (end < totalPages) {
        if (end < totalPages - 1) pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  // ─── Contact tab handlers ────────────────────────────────────────────────
  // Parallel to the debt-tab handleSort/handlePageChange/handleDrop etc.
  // Kept separate (rather than refactoring the originals to be tab-generic)
  // so the existing tabs stay untouched. handleDragStart and handleDragOver
  // are NOT duplicated — they don't reference tab-specific state.
  const handleContactSort = (key) => {
    let direction = 'asc';
    if (contactSortConfig.key === key && contactSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setContactSortConfig({ key, direction });
    setContactCurrentPage(1);
  };

  const getContactSortIcon = (columnKey) => {
    if (contactSortConfig.key !== columnKey) return ' ↕️';
    return contactSortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const handleContactPageChange = (newPage) => setContactCurrentPage(newPage);

  const handleContactItemsPerPageChange = (newItemsPerPage) => {
    setContactItemsPerPage(newItemsPerPage);
    setContactCurrentPage(1);
  };

  const getContactPageNumbers = (totalPages) => {
    const pageNumbers = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      const start = Math.max(1, contactCurrentPage - Math.floor(maxVisible / 2));
      const end = Math.min(totalPages, start + maxVisible - 1);
      if (start > 1) {
        pageNumbers.push(1);
        if (start > 2) pageNumbers.push('...');
      }
      for (let i = start; i <= end; i++) pageNumbers.push(i);
      if (end < totalPages) {
        if (end < totalPages - 1) pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    return pageNumbers;
  };

  const handleContactDrop = (e, targetColumnKey) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === targetColumnKey) {
      setDraggedColumn(null);
      return;
    }
    const newOrder = [...contactColumnOrder];
    const draggedIndex = newOrder.indexOf(draggedColumn);
    const targetIndex = newOrder.indexOf(targetColumnKey);
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedColumn);
    setContactColumnOrder(newOrder);
    setDraggedColumn(null);
  };

  // Enhanced validation functions
  const validateRow = (row) => {
    const errors = {};
    
    if (!row.debt_id || row.debt_id === '') {
      errors.debt_id = 'Debt ID is required';
    }
    
    if (row.has_summons !== undefined && row.has_summons !== null && row.has_summons !== '') {
      const hasSummonsNum = Number(row.has_summons);
      if (isNaN(hasSummonsNum) || (hasSummonsNum !== 0 && hasSummonsNum !== 1)) {
        errors.has_summons = 'Has Summons must be 0 or 1';
      }
    }
    
    if (row.poa_sent_date && row.poa_sent_date !== '') {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(row.poa_sent_date)) {
        errors.poa_sent_date = 'Invalid date format (YYYY-MM-DD)';
      } else {
        const date = new Date(row.poa_sent_date);
        if (isNaN(date.getTime())) {
          errors.poa_sent_date = 'Invalid date';
        } else {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (date > today) {
            errors.poa_sent_date = 'POA Sent Date cannot be in the future';
          }
        }
      }
    }
    
    if (row.summons_date && row.summons_date !== '') {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(row.summons_date)) {
        errors.summons_date = 'Invalid date format (YYYY-MM-DD)';
      } else {
        const date = new Date(row.summons_date);
        if (isNaN(date.getTime())) {
          errors.summons_date = 'Invalid date';
        } else {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (date > today) {
            errors.summons_date = 'Summons Date cannot be in the future';
          }
        }
      }
    }
    
    if (row.service_date && row.service_date !== '') {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(row.service_date)) {
        errors.service_date = 'Invalid date format (YYYY-MM-DD)';
      } else {
        const date = new Date(row.service_date);
        if (isNaN(date.getTime())) {
          errors.service_date = 'Invalid date';
        } else {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (date > today) {
            errors.service_date = 'Service Date cannot be in the future';
          }
        }
      }
    }

    if (row.voided_date && row.voided_date !== '') {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(row.voided_date)) {
        errors.voided_date = 'Invalid date format (YYYY-MM-DD)';
      } else {
        const date = new Date(row.voided_date);
        if (isNaN(date.getTime())) {
          errors.voided_date = 'Invalid date';
        } else {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (date > today) {
            errors.voided_date = 'Voided Date cannot be in the future';
          }
        }
      }
    }

    if (row.voided_sif_remaining_client_obligation !== undefined &&
        row.voided_sif_remaining_client_obligation !== null &&
        row.voided_sif_remaining_client_obligation !== '') {
      const amount = Number(row.voided_sif_remaining_client_obligation);
      if (isNaN(amount) || amount < 0) {
        errors.voided_sif_remaining_client_obligation = 'Must be a non-negative number';
      }
    }

    
    if (row.prelegal !== undefined && row.prelegal !== null && row.prelegal !== '') {
      const prelegalNum = Number(row.prelegal);
      if (isNaN(prelegalNum) || (prelegalNum !== 0 && prelegalNum !== 1)) {
        errors.prelegal = 'Prelegal must be 0 or 1';
      }
    }
    
    return errors;
  };

  // ─── Contact tab validation ──────────────────────────────────────────────
  // contact_id is the only required field. Every other field is optional —
  // blank values pass through silently. But IF a value is provided, it must
  // be well-formed:
  //   - Dates: YYYY-MM-DD and a real calendar date (future dates allowed
  //     since some contact events legitimately schedule forward).
  //   - loan_funded_amount: must parse to a non-negative number.
  // Text fields (loan_partner / loan_outreach_status / pdr_loan_status)
  // have no obvious malformed state and pass through unchecked.
  const validateContactRow = (row) => {
    const errors = {};

    if (!row.contact_id || row.contact_id === '') {
      errors.contact_id = 'Contact ID is required';
    }

    const checkDate = (fieldKey) => {
      const value = row[fieldKey];
      if (!value || value === '') return;
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(value)) {
        errors[fieldKey] = 'Invalid date format (YYYY-MM-DD)';
        return;
      }
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        errors[fieldKey] = 'Invalid date';
      }
    };

    checkDate('loan_funded_date');
    checkDate('last_contact_date');
    checkDate('last_check_in_attempt');
    checkDate('last_check_in_completed');

    if (row.loan_funded_amount !== undefined &&
        row.loan_funded_amount !== null &&
        row.loan_funded_amount !== '') {
      const amount = Number(row.loan_funded_amount);
      if (isNaN(amount) || amount < 0) {
        errors.loan_funded_amount = 'Must be a non-negative number';
      }
    }

    return errors;
  };

  const validateAllContactRows = (data) => {
    const allErrors = {};
    data.forEach((row, index) => {
      const rowErrors = validateContactRow(row);
      if (Object.keys(rowErrors).length > 0) {
        allErrors[index] = rowErrors;
      }
    });
    return allErrors;
  };

  const isAllContactRowsValid = () => Object.keys(contactValidationErrors).length === 0;

  const validateAllRows = (data) => {
    const allErrors = {};
    data.forEach((row, index) => {
      const rowErrors = validateRow(row);
      if (Object.keys(rowErrors).length > 0) {
        allErrors[index] = rowErrors;
      }
    });
    return allErrors;
  };

  const csvToJSON = (csv) => {
    try {
      const lines = csv.split('\n').filter(line => line.trim() !== '');
      if (lines.length < 2) {
        throw new Error('CSV must have at least a header row and one data row');
      }
      
      const result = [];
      
      // Parse CSV line considering quoted fields
      const parseCSVLine = (line) => {
        const fields = [];
        let field = '';
        let insideQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          const nextChar = line[i + 1];
          
          if (char === '"') {
            if (insideQuotes && nextChar === '"') {
              // Escaped quote
              field += '"';
              i++; // Skip next quote
            } else {
              // Toggle quote state
              insideQuotes = !insideQuotes;
            }
          } else if (char === ',' && !insideQuotes) {
            // Field separator
            fields.push(field);
            field = '';
          } else {
            field += char;
          }
        }
        fields.push(field); // Add last field
        return fields;
      };
      
      const convertToYYYYMMDD = (dateStr) => {
        if (!dateStr || dateStr.trim() === '') return '';
        dateStr = dateStr.trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
          const parts = dateStr.split('/');
          return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
          const parts = dateStr.split('/');
          const month = parts[0].padStart(2, '0');
          const day = parts[1].padStart(2, '0');
          return `${parts[2]}-${month}-${day}`;
        }
        if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
          const parts = dateStr.split('-');
          return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(dateStr)) {
          const parts = dateStr.split('-');
          const month = parts[0].padStart(2, '0');
          const day = parts[1].padStart(2, '0');
          return `${parts[2]}-${month}-${day}`;
        }
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
        return dateStr;
      };
      
      const headers = parseCSVLine(lines[0]).map(header => header.trim());
      
      console.log('CSV Headers:', headers);
      
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        const obj = {};
        
        const currentLine = parseCSVLine(lines[i]);
        
        for (let j = 0; j < headers.length; j++) {
          let value = currentLine[j] ? currentLine[j].trim() : '';
          const standardFieldName = getStandardFieldName(headers[j]);
          
          // Handle date fields first
          if (standardFieldName === 'poa_sent_date' ||
              standardFieldName === 'summons_date' ||
              standardFieldName === 'service_date' ||
              standardFieldName === 'voided_date') {
            value = convertToYYYYMMDD(value);
          } else if (standardFieldName === 'voided_sif_id') {
            // Force-string fields: preserve original value verbatim (e.g. leading zeros, alphanumeric IDs)
            // value is already a trimmed string from above
          } else {
            // Handle boolean and numeric conversions for non-date fields
            if (value.toLowerCase() === 'true') {
              value = true;
            } else if (value.toLowerCase() === 'false') {
              value = false;
            } else if (!isNaN(value) && value !== '' && !isNaN(parseFloat(value))) {
              value = parseFloat(value);
            }
          }
          
          obj[standardFieldName] = value;
        }
        
        // Normalize has_summons to 0 or 1
        if (obj.has_summons === true) obj.has_summons = 1;
        else if (obj.has_summons === false) obj.has_summons = 0;
        else if (typeof obj.has_summons === 'string' && obj.has_summons.toLowerCase() === 'true') obj.has_summons = 1;
        else if (typeof obj.has_summons === 'string' && obj.has_summons.toLowerCase() === 'false') obj.has_summons = 0;
        
        // Normalize prelegal to 0 or 1
        if (obj.prelegal === true) obj.prelegal = 1;
        else if (obj.prelegal === false) obj.prelegal = 0;
        else if (typeof obj.prelegal === 'string' && obj.prelegal.toLowerCase() === 'yes') obj.prelegal = 1;
        else if (typeof obj.prelegal === 'string' && obj.prelegal.toLowerCase() === 'no') obj.prelegal = 0;
        
        result.push(obj);
      }
      
      console.log('Parsed CSV data:', result);
      return result;
    } catch (error) {
      console.error('CSV parsing error:', error);
      throw new Error('Failed to parse CSV file: ' + error.message);
    }
  };

  const getStandardFieldName = (fieldName) => {
    const field = fieldName.toLowerCase().replace(/[^a-z0-9]/g, '_');
    const fieldMap = {
      'debt_id': 'debt_id', 'has_summons': 'has_summons', 'poa_sent_date': 'poa_sent_date',
      'summons_date': 'summons_date', 'service_date': 'service_date', 'legal_negotiator': 'legal_negotiator',
      'legal_resolution_status': 'legal_resolution_status', 'poa_type': 'poa_type',
      'prelegal': 'prelegal', 'pre_legal': 'prelegal',
      'sif_auth': 'sif_auth', 'sif_letter': 'sif_letter',
      'void_reason': 'void_reason',
      'voided_date': 'voided_date',
      'voided_sif_id': 'voided_sif_id',
      'voided_sif_remaining_client_obligation': 'voided_sif_remaining_client_obligation',
      'notes': 'notes'
    };
    return fieldMap[field] || fieldName;
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateString;
  };

  const showStatus = (message, isSuccess, isHistory = false) => {
    if (isHistory) {
      setHistoryStatus({ message, isSuccess, show: true });
      setTimeout(() => setHistoryStatus(prev => ({ ...prev, show: false })), 5000);
    } else {
      setStatus({ message, isSuccess, show: true });
      setTimeout(() => setStatus(prev => ({ ...prev, show: false })), 5000);
    }
  };

  const handleCellEdit = (rowIndex, field, value) => {
    const newEditedData = { ...editedData };
    if (!newEditedData[rowIndex]) newEditedData[rowIndex] = {};
    newEditedData[rowIndex][field] = value;
    setEditedData(newEditedData);

    // Build the row to validate by merging: original CSV row → all previously
    // edited fields for this row → the current cell change. Without including
    // previous edits, validation runs against stale (still-invalid) data for
    // fields the user has already corrected, causing those fields to flip back
    // to invalid the moment another field on the same row is edited.
    const currentData = uploadedCsvData[rowIndex];
    const editedRow = {
      ...currentData,
      ...(newEditedData[rowIndex] || {}),
    };
    const rowErrors = validateRow(editedRow);

    const newValidationErrors = { ...validationErrors };
    if (Object.keys(rowErrors).length > 0) {
      newValidationErrors[rowIndex] = rowErrors;
    } else {
      delete newValidationErrors[rowIndex];
    }
    setValidationErrors(newValidationErrors);
  };


  const getCellValue = (rowIndex, field, originalValue) => {
    if (editedData[rowIndex] && editedData[rowIndex][field] !== undefined) {
      return editedData[rowIndex][field];
    }
    return originalValue;
  };

  const isAllRowsValid = () => Object.keys(validationErrors).length === 0;

  const getValidationSummary = () => {
    const errorCount = Object.keys(validationErrors).length;
    const totalRows = uploadedCsvData ? uploadedCsvData.length : 0;
    
    if (errorCount === 0 && totalRows > 0) {
      return { message: `All ${totalRows} rows are valid and ready for upload`, isValid: true };
    } else if (errorCount > 0) {
      return { message: `${errorCount} row(s) have validation errors that need to be fixed`, isValid: false };
    } else {
      return { message: 'No data to validate', isValid: false };
    }
  };

  const handleFileUpload = async () => {
    if (!file) {
      showStatus('Please select a CSV file', false);
      return;
    }

    try {
      setIsLoading(true);
      const csvText = await file.text();
      const jsonData = csvToJSON(csvText);

      if (jsonData.length === 0) {
        showStatus('CSV file appears to be empty or invalid', false);
        return;
      }

      const currentDate = new Date().toISOString().split('T')[0];
      const enrichedJsonData = jsonData.map(record => ({
        ...record,
        summons_process_date: currentDate
      }));

      setUploadedCsvData(enrichedJsonData);
      const validationErrors = validateAllRows(enrichedJsonData);
      setValidationErrors(validationErrors);
      setIsPreviewMode(true);
      setResponseData(null);

      const errorCount = Object.keys(validationErrors).length;
      if (errorCount > 0) {
        showStatus(`CSV loaded with ${errorCount} validation error(s). Please review and fix errors before uploading.`, false);
      } else {
        showStatus('CSV loaded successfully! All data is valid. Click "Confirm Upload" to proceed.', true);
      }
      setCurrentPage(1);
    } catch (error) {
      showStatus('Error processing file: ' + error.message, false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmUpload = async () => {
    if (!uploadedCsvData || uploadedCsvData.length === 0) {
      showStatus('No data to upload. Please upload a CSV file first.', false);
      return;
    }

    if (!isAllRowsValid()) {
      showStatus('Please fix all validation errors before uploading.', false);
      return;
    }

    try {
      setIsLoading(true);
      const dataToUpload = uploadedCsvData.map((row, index) => {
        if (editedData[index]) return { ...row, ...editedData[index] };
        return row;
      });

      const response = await fetch('https://eonvuoowi4m7l7w.m.pipedream.net', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToUpload)
      });

      const responseText = await response.text();

      if (response.ok) {
        try {
          const responseDataResult = JSON.parse(responseText);
          showStatus('Bulk update data uploaded successfully!', true);
          setResponseData({ status: response.status, data: responseDataResult });
        } catch (parseError) {
          showStatus('Bulk update data uploaded successfully!', true);
          setResponseData({
            status: response.status,
            data: { summonsRecordUpdates: [], summonsNotes: [], rawResponse: responseText }
          });
        }

        setFile(null);
        // Persist the merged (corrected) data so Upload Results reflects what
        // was actually sent. Without this, the results table re-renders against
        // the original uploadedCsvData and the edits the user made in preview
        // appear to have been silently discarded.
        setUploadedCsvData(dataToUpload);
        setIsPreviewMode(false);
        setValidationErrors({});
        setEditedData({});
        setCurrentPage(1);
      } else {
        showStatus(`Upload failed with status ${response.status}: ${responseText}`, false);
      }
    } catch (error) {
      showStatus('Error uploading data: ' + error.message, false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelPreview = () => {
    setIsPreviewMode(false);
    setValidationErrors({});
    setEditedData({});
    setFile(null);
    setUploadedCsvData(null);
    setCurrentPage(1);
    showStatus('Preview cancelled. You can upload a new file.', true);
  };

  // ─── Contact tab status + parsing + handlers ─────────────────────────────
  const showContactStatus = (message, isSuccess) => {
    setContactStatus({ message, isSuccess, show: true });
    setTimeout(() => setContactStatus(prev => ({ ...prev, show: false })), 5000);
  };

  // Standalone CSV parser for the Contact tab. Mirrors csvToJSON's quoted-
  // field handling and date format coercion but uses getContactStandardFieldName
  // for header mapping and normalizes only the contact-level field set.
  // Kept separate (not refactored to share with csvToJSON) to limit blast
  // radius — the existing parser stays untouched and continues working for
  // debt-level uploads.
  const contactCsvToJSON = (csv) => {
    try {
      const lines = csv.split('\n').filter(line => line.trim() !== '');
      if (lines.length < 2) throw new Error('CSV must have at least a header row and one data row');

      const parseCSVLine = (line) => {
        const fields = [];
        let field = '';
        let insideQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          const nextChar = line[i + 1];
          if (char === '"') {
            if (insideQuotes && nextChar === '"') { field += '"'; i++; }
            else { insideQuotes = !insideQuotes; }
          } else if (char === ',' && !insideQuotes) {
            fields.push(field); field = '';
          } else { field += char; }
        }
        fields.push(field);
        return fields;
      };

      const convertToYYYYMMDD = (dateStr) => {
        if (!dateStr || dateStr.trim() === '') return '';
        dateStr = dateStr.trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
        if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
          const parts = dateStr.split('/');
          return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
        }
        if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(dateStr)) {
          const parts = dateStr.split('-');
          return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
        }
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          const y = date.getFullYear();
          const m = String(date.getMonth() + 1).padStart(2, '0');
          const d = String(date.getDate()).padStart(2, '0');
          return `${y}-${m}-${d}`;
        }
        return dateStr;
      };

      const CONTACT_DATE_FIELDS = new Set([
        'loan_funded_date', 'last_contact_date',
        'last_check_in_attempt', 'last_check_in_completed',
      ]);

      const headers = parseCSVLine(lines[0]).map(h => h.trim());
      const result = [];
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        const fields = parseCSVLine(lines[i]);
        const obj = {};
        for (let j = 0; j < headers.length; j++) {
          let value = fields[j] ? fields[j].trim() : '';
          const key = getContactStandardFieldName(headers[j]);
          if (CONTACT_DATE_FIELDS.has(key)) {
            value = convertToYYYYMMDD(value);
          } else if (key === 'loan_funded_amount') {
            // Leave amount as a string so the type="text" input in preview
            // can surface invalid CSV values for the user to correct (rather
            // than silently coercing "abc" to NaN). validateContactRow runs
            // Number() to flag bad input.
          } else if (key === 'contact_id') {
            // Force-string: preserve leading zeros and alphanumeric IDs.
          } else if (!isNaN(value) && value !== '' && !isNaN(parseFloat(value))) {
            value = parseFloat(value);
          }
          obj[key] = value;
        }
        result.push(obj);
      }
      return result;
    } catch (err) {
      throw new Error('Failed to parse CSV file: ' + err.message);
    }
  };

  const handleContactFileUpload = async () => {
    if (!contactFile) {
      showContactStatus('Please select a CSV file', false);
      return;
    }
    try {
      setIsLoading(true);
      const csvText = await contactFile.text();
      const jsonData = contactCsvToJSON(csvText);
      if (jsonData.length === 0) {
        showContactStatus('CSV file appears to be empty or invalid', false);
        return;
      }
      setContactUploadedCsvData(jsonData);
      const errors = validateAllContactRows(jsonData);
      setContactValidationErrors(errors);
      setContactIsPreviewMode(true);
      setContactResponseData(null);
      const errorCount = Object.keys(errors).length;
      if (errorCount > 0) {
        showContactStatus(`CSV loaded with ${errorCount} validation error(s). Please review and fix errors before uploading.`, false);
      } else {
        showContactStatus('CSV loaded successfully! All data is valid. Click "Confirm Upload" to proceed.', true);
      }
      setContactCurrentPage(1);
    } catch (err) {
      showContactStatus('Error processing file: ' + err.message, false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactConfirmUpload = async () => {
    if (!contactUploadedCsvData || contactUploadedCsvData.length === 0) {
      showContactStatus('No data to upload. Please upload a CSV file first.', false);
      return;
    }
    if (!isAllContactRowsValid()) {
      showContactStatus('Please fix all validation errors before uploading.', false);
      return;
    }
    try {
      setIsLoading(true);
      const dataToUpload = contactUploadedCsvData.map((row, index) =>
        contactEditedData[index] ? { ...row, ...contactEditedData[index] } : row
      );

      // Pipedream workflow that handles contact-level updates. Receives an
      // array of contact-level records keyed by contact_id and applies the
      // 8 loan/contact fields to Forth on the contact record.
      const CONTACT_UPDATE_ENDPOINT = 'https://eo97h60sa1y1tiy.m.pipedream.net';

      const response = await fetch(CONTACT_UPDATE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToUpload),
      });
      const responseText = await response.text();

      if (response.ok) {
        try {
          const responseDataResult = JSON.parse(responseText);
          showContactStatus('Contact update data uploaded successfully!', true);
          setContactResponseData({ status: response.status, data: responseDataResult });
        } catch {
          showContactStatus('Contact update data uploaded successfully!', true);
          setContactResponseData({ status: response.status, data: { rawResponse: responseText } });
        }
        setContactFile(null);
        setContactUploadedCsvData(dataToUpload);
        setContactIsPreviewMode(false);
        setContactValidationErrors({});
        setContactEditedData({});
        setContactCurrentPage(1);
      } else {
        showContactStatus(`Upload failed with status ${response.status}: ${responseText}`, false);
      }
    } catch (err) {
      showContactStatus('Error uploading data: ' + err.message, false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactCancelPreview = () => {
    setContactIsPreviewMode(false);
    setContactValidationErrors({});
    setContactEditedData({});
    setContactFile(null);
    setContactUploadedCsvData(null);
    setContactCurrentPage(1);
    showContactStatus('Preview cancelled. You can upload a new file.', true);
  };

  // Mirrors handleCellEdit — re-runs validateContactRow against the merged
  // edited row so multi-field corrections don't flip already-fixed fields
  // back to invalid (same bug pattern as the debt-tab handleCellEdit).
  const handleContactCellEdit = (rowIndex, field, value) => {
    const newEditedData = { ...contactEditedData };
    if (!newEditedData[rowIndex]) newEditedData[rowIndex] = {};
    newEditedData[rowIndex][field] = value;
    setContactEditedData(newEditedData);

    const currentData = contactUploadedCsvData[rowIndex];
    const editedRow = { ...currentData, ...(newEditedData[rowIndex] || {}) };
    const rowErrors = validateContactRow(editedRow);

    const newValidationErrors = { ...contactValidationErrors };
    if (Object.keys(rowErrors).length > 0) {
      newValidationErrors[rowIndex] = rowErrors;
    } else {
      delete newValidationErrors[rowIndex];
    }
    setContactValidationErrors(newValidationErrors);
  };

  const getContactCellValue = (rowIndex, field, originalValue) => {
    if (contactEditedData[rowIndex] && contactEditedData[rowIndex][field] !== undefined) {
      return contactEditedData[rowIndex][field];
    }
    return originalValue;
  };

  const getContactValidationSummary = () => {
    const errorCount = Object.keys(contactValidationErrors).length;
    const totalRows = contactUploadedCsvData ? contactUploadedCsvData.length : 0;
    if (errorCount === 0 && totalRows > 0) {
      return { message: `All ${totalRows} rows are valid and ready for upload`, isValid: true };
    } else if (errorCount > 0) {
      return { message: `${errorCount} row(s) have validation errors that need to be fixed`, isValid: false };
    } else {
      return { message: 'No data to validate', isValid: false };
    }
  };

  // Renders the contact-tab preview/results table. Mirrors renderUploadTable's
  // design: validation banner, items-per-page selector with smart page-number
  // navigation, sortable+draggable column headers with sticky positioning,
  // and a section header that switches between "Data Preview" (in preview
  // mode) and "Upload Results" (after upload). Edit-mode inputs branch on
  // field type — 4 dates with max=today, 1 currency as text+inputMode="decimal"
  // (avoids the type="number" locked-cell bug), the rest plain text.
  const renderContactUploadTable = () => {
    // Tag each row with its stable original index BEFORE sorting. contactEditedData
    // and contactValidationErrors are keyed by the row's position in
    // contactUploadedCsvData (the unsorted array) — that's also how edits are
    // merged back at upload time. Sorting reorders the rows, so we must look up
    // and write edits by __originalIndex, never by the post-sort position.
    let dataArray = contactUploadedCsvData
      ? contactUploadedCsvData.map((row, i) => ({ ...row, __originalIndex: i }))
      : [];

    if (contactSortConfig.key && dataArray.length > 0) {
      dataArray = dataArray.sort((a, b) => {
        const aValue = a[contactSortConfig.key] || '';
        const bValue = b[contactSortConfig.key] || '';
        // loan_funded_amount is the one numeric column on this tab; everything
        // else (including dates as YYYY-MM-DD strings) sorts lexicographically.
        const numericColumns = ['loan_funded_amount'];
        if (numericColumns.includes(contactSortConfig.key)) {
          const aNum = parseFloat(aValue) || 0;
          const bNum = parseFloat(bValue) || 0;
          return contactSortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();
        if (aStr < bStr) return contactSortConfig.direction === 'asc' ? -1 : 1;
        if (aStr > bStr) return contactSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    const totalPages = Math.ceil(dataArray.length / contactItemsPerPage);
    const startIndex = (contactCurrentPage - 1) * contactItemsPerPage;
    const endIndex = startIndex + contactItemsPerPage;
    const paginatedData = dataArray.slice(startIndex, endIndex);
    const validationSummary = contactIsPreviewMode ? getContactValidationSummary() : null;

    const CONTACT_DATE_FIELDS = new Set([
      'loan_funded_date', 'last_contact_date',
      'last_check_in_attempt', 'last_check_in_completed',
    ]);
    const CONTACT_TEXT_FIELDS = new Set([
      'loan_partner', 'loan_outreach_status', 'pdr_loan_status',
    ]);
    // No `max` constraint on the contact-tab date pickers — contact dates
    // may legitimately be future (e.g. scheduled check-in).

    return (
      <div>
        {contactIsPreviewMode && validationSummary && (
          <div style={{
            backgroundColor: validationSummary.isValid ? '#dcfce7' : '#fef2f2',
            border: `1px solid ${validationSummary.isValid ? '#bbf7d0' : '#fecaca'}`,
            borderRadius: '8px', padding: '12px', marginBottom: '12px',
            fontSize: '12px', fontWeight: '500',
            color: validationSummary.isValid ? '#166534' : '#dc2626'
          }}>
            {validationSummary.message}
            {!validationSummary.isValid && (
              <div style={{ marginTop: '4px', fontSize: '11px', fontWeight: 'normal' }}>
                Please fix the highlighted errors in the table below before uploading.
              </div>
            )}
          </div>
        )}

        {dataArray.length > 0 && (
          <div style={{
            backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb',
            padding: '12px', marginBottom: '12px', display: 'flex',
            justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>Show:</label>
              <select value={contactItemsPerPage} onChange={(e) => handleContactItemsPerPageChange(Number(e.target.value))}
                style={{ border: '1px solid #d1d5db', borderRadius: '4px', padding: '3px 6px',
                  fontSize: '11px', backgroundColor: 'white', cursor: 'pointer', width: '70px' }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>entries per page</span>
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              Showing {startIndex + 1} to {Math.min(endIndex, dataArray.length)} of {dataArray.length} entries
              {contactIsPreviewMode && <span style={{ marginLeft: '8px', color: '#2563eb' }}>• Preview Mode</span>}
            </div>
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button onClick={() => handleContactPageChange(contactCurrentPage - 1)} disabled={contactCurrentPage === 1}
                  style={{ padding: '4px 8px', fontSize: '11px', border: '1px solid #d1d5db',
                    borderRadius: '4px', backgroundColor: contactCurrentPage === 1 ? '#f9fafb' : 'white',
                    color: contactCurrentPage === 1 ? '#9ca3af' : '#374151',
                    cursor: contactCurrentPage === 1 ? 'not-allowed' : 'pointer' }}>
                  Previous
                </button>
                {getContactPageNumbers(totalPages).map((pageNum, index) => (
                  <button key={index} onClick={() => typeof pageNum === 'number' && handleContactPageChange(pageNum)}
                    disabled={pageNum === '...'}
                    style={{ padding: '4px 8px', fontSize: '11px', border: '1px solid #d1d5db',
                      borderRadius: '4px', backgroundColor: pageNum === contactCurrentPage ? '#2563eb' : 'white',
                      color: pageNum === contactCurrentPage ? 'white' : pageNum === '...' ? '#9ca3af' : '#374151',
                      cursor: pageNum === '...' ? 'default' : 'pointer',
                      fontWeight: pageNum === contactCurrentPage ? '500' : 'normal' }}>
                    {pageNum}
                  </button>
                ))}
                <button onClick={() => handleContactPageChange(contactCurrentPage + 1)} disabled={contactCurrentPage === totalPages}
                  style={{ padding: '4px 8px', fontSize: '11px', border: '1px solid #d1d5db',
                    borderRadius: '4px', backgroundColor: contactCurrentPage === totalPages ? '#f9fafb' : 'white',
                    color: contactCurrentPage === totalPages ? '#9ca3af' : '#374151',
                    cursor: contactCurrentPage === totalPages ? 'not-allowed' : 'pointer' }}>
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <div style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
            <h2 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0 }}>
              {contactIsPreviewMode ? `Data Preview (${dataArray.length} records)` : `Upload Results (${dataArray.length} records)`}
              {dataArray.length > 0 ? (
                <span style={{
                  color: contactIsPreviewMode ? '#2563eb' : '#059669',
                  fontSize: '12px',
                  marginLeft: '8px'
                }}>
                  • {contactIsPreviewMode ? 'Review & Edit' : 'Data Loaded'}
                </span>
              ) : (
                <span style={{ color: '#6b7280', fontSize: '12px', marginLeft: '8px' }}>• No data yet</span>
              )}
            </h2>
          </div>

          <div style={{ overflow: 'auto', maxHeight: '60vh', border: '1px solid #e5e7eb' }}>
            <table style={{ minWidth: '100%', fontSize: '12px', color: '#374151' }}>
              <thead style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0, zIndex: 10, borderBottom: '1px solid #d1d5db' }}>
                <tr>
                  {contactColumnOrder.map((key, index) => (
                    <th key={key}
                      draggable
                      onDragStart={(e) => handleDragStart(e, key)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleContactDrop(e, key)}
                      onClick={() => handleContactSort(key)}
                      style={{
                        padding: '8px', textAlign: 'left',
                        borderRight: index < contactColumnOrder.length - 1 ? '1px solid #d1d5db' : 'none',
                        width: contactColumnDefinitions[key].width, fontWeight: '600',
                        cursor: 'pointer', userSelect: 'none',
                        backgroundColor: draggedColumn === key ? '#e5e7eb' : '#f9fafb'
                      }}
                      title="Click to sort • Drag to reorder">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>{contactColumnDefinitions[key].name}</span>
                        <span style={{ fontSize: '10px', opacity: 0.7 }}>{getContactSortIcon(key)}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dataArray.length === 0 ? (
                  <tr>
                    <td colSpan={contactColumnOrder.length} style={{
                      padding: '40px', textAlign: 'center', color: '#6b7280',
                      fontSize: '14px', fontStyle: 'italic'
                    }}>
                      No data uploaded yet. Upload a CSV file to see data here.
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((row) => {
                    // Use the stable original index (survives sorting) so edited
                    // values, validation errors, and edit writes all line up with
                    // the correct source row.
                    const actualIndex = row.__originalIndex;
                    const rowErrors = contactValidationErrors[actualIndex] || {};
                    const hasErrors = Object.keys(rowErrors).length > 0;

                    return (
                      <tr key={actualIndex} style={{
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: hasErrors ? '#fef2f2' : 'transparent'
                      }}>
                        {contactColumnOrder.map((key, colIndex) => {
                          const cellValue = getContactCellValue(actualIndex, key, row[key]);
                          const cellError = rowErrors[key];

                          return (
                            <td key={key} style={{
                              padding: '8px',
                              borderRight: colIndex < contactColumnOrder.length - 1 ? '1px solid #e5e7eb' : 'none',
                              backgroundColor: cellError ? '#fef2f2' : 'transparent',
                              border: cellError ? '1px solid #fecaca' : 'none',
                              position: 'relative'
                            }}
                            title={cellError ? cellError : ''}>
                              {contactIsPreviewMode ? (
                                <div style={{ position: 'relative' }}>
                                  {key === 'contact_id' && (
                                    <input
                                      type="text" value={cellValue == null ? '' : cellValue}
                                      onChange={(e) => handleContactCellEdit(actualIndex, key, e.target.value)}
                                      style={{
                                        width: '100%', padding: '2px 4px', fontSize: '11px',
                                        border: cellError ? '2px solid #dc2626' : '1px solid #d1d5db',
                                        borderRadius: '3px', backgroundColor: cellError ? '#fef2f2' : 'white'
                                      }}
                                    />
                                  )}
                                  {CONTACT_DATE_FIELDS.has(key) && (
                                    <input
                                      type="date" value={cellValue || ''}
                                      onChange={(e) => handleContactCellEdit(actualIndex, key, e.target.value)}
                                      style={{
                                        width: '100%', padding: '2px 4px', fontSize: '11px',
                                        border: cellError ? '2px solid #dc2626' : '1px solid #d1d5db',
                                        borderRadius: '3px', backgroundColor: cellError ? '#fef2f2' : 'white'
                                      }}
                                    />
                                  )}
                                  {key === 'loan_funded_amount' && (
                                    // type="text" + inputMode="decimal" — mirrors the debt-tab
                                    // voided_sif_remaining_client_obligation input. Native number
                                    // inputs silently reject any non-numeric value, leaving cells
                                    // empty/locked for invalid CSV values.
                                    <input
                                      type="text" inputMode="decimal"
                                      value={cellValue == null ? '' : cellValue}
                                      onChange={(e) => handleContactCellEdit(actualIndex, key, e.target.value)}
                                      style={{
                                        width: '100%', padding: '2px 4px', fontSize: '11px',
                                        border: cellError ? '2px solid #dc2626' : '1px solid #d1d5db',
                                        borderRadius: '3px', backgroundColor: cellError ? '#fef2f2' : 'white'
                                      }}
                                    />
                                  )}
                                  {CONTACT_TEXT_FIELDS.has(key) && (
                                    <input
                                      type="text" value={cellValue || ''}
                                      onChange={(e) => handleContactCellEdit(actualIndex, key, e.target.value)}
                                      style={{
                                        width: '100%', padding: '2px 4px', fontSize: '11px',
                                        border: cellError ? '2px solid #dc2626' : '1px solid #d1d5db',
                                        borderRadius: '3px', backgroundColor: cellError ? '#fef2f2' : 'white'
                                      }}
                                    />
                                  )}
                                  {cellError && (
                                    <div style={{ fontSize: '10px', color: '#dc2626', marginTop: '2px' }}>
                                      {cellError}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <>
                                  {CONTACT_DATE_FIELDS.has(key) && (row[key] ? formatDate(row[key]) : '')}
                                  {key === 'loan_funded_amount' &&
                                    (row[key] !== undefined && row[key] !== null && row[key] !== ''
                                      ? formatCurrency(row[key]) : '')}
                                  {(key === 'contact_id' || CONTACT_TEXT_FIELDS.has(key)) &&
                                    (row[key] || '')}
                                </>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // History endpoints — Debt has a live Pipedream workflow; Contact history
  // is wired ahead of the backend. Swap CONTACT_HISTORY_ENDPOINT when the
  // contact-level history workflow is provisioned. Both endpoints are
  // expected to accept start_date + end_date as query params and return a
  // JSON array of history rows in their respective schemas.
  const DEBT_HISTORY_ENDPOINT    = 'https://eovk9hyi0htj5gc.m.pipedream.net';
  const CONTACT_HISTORY_ENDPOINT = 'https://eogipq6ri3h6hqz.m.pipedream.net';

  const fetchHistory = async () => {
    if (!startDate || !endDate) {
      showStatus('Please select both start and end dates', false, true);
      return;
    }

    const endpoint = historyType === 'contact' ? CONTACT_HISTORY_ENDPOINT : DEBT_HISTORY_ENDPOINT;
    const label    = historyType === 'contact' ? 'Contact'                : 'Debt';

    try {
      setIsLoading(true);
      const response = await fetch(`${endpoint}?start_date=${startDate}&end_date=${endDate}`);

      if (response.ok) {
        const data = await response.json();
        showStatus(`${label}-level update history retrieved successfully!`, true, true);
        setHistoryData(data);
        setCurrentPage(1);
      } else {
        showStatus(`Failed to retrieve ${label.toLowerCase()}-level history. Please try again.`, false, true);
      }
    } catch (error) {
      showStatus('Error fetching history: ' + error.message, false, true);
    } finally {
      setIsLoading(false);
    }
  };

  // When the user flips the Update Type toggle, the existing historyData has
  // the wrong column shape — clear it so the table renders its empty state
  // rather than columns of em-dashes.
  useEffect(() => {
    setHistoryData(null);
    setCurrentPage(1);
  }, [historyType]);

  // Render upload table with sorting and pagination
  const renderUploadTable = () => {
    // Tag each row with its stable original index BEFORE sorting. editedData and
    // validationErrors are keyed by the row's position in uploadedCsvData (the
    // unsorted array) — that's also how edits are merged back at upload time.
    // Sorting reorders the rows, so we must look up and write edits by
    // __originalIndex, never by the post-sort position.
    let dataArray = uploadedCsvData
      ? uploadedCsvData.map((row, i) => ({ ...row, __originalIndex: i }))
      : [];

    if (sortConfig.key && dataArray.length > 0) {
      dataArray = dataArray.sort((a, b) => {
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';
        
        const numericColumns = ['debt_id', 'voided_sif_remaining_client_obligation'];
        if (numericColumns.includes(sortConfig.key)) {
          const aNum = parseFloat(aValue) || 0;
          const bNum = parseFloat(bValue) || 0;
          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }
        
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();
        
        if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    const totalPages = Math.ceil(dataArray.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = dataArray.slice(startIndex, endIndex);

    const validationSummary = isPreviewMode ? getValidationSummary() : null;

    return (
      <div>
        {isPreviewMode && validationSummary && (
          <div style={{
            backgroundColor: validationSummary.isValid ? '#dcfce7' : '#fef2f2',
            border: `1px solid ${validationSummary.isValid ? '#bbf7d0' : '#fecaca'}`,
            borderRadius: '8px', padding: '12px', marginBottom: '12px',
            fontSize: '12px', fontWeight: '500',
            color: validationSummary.isValid ? '#166534' : '#dc2626'
          }}>
            {validationSummary.message}
            {!validationSummary.isValid && (
              <div style={{ marginTop: '4px', fontSize: '11px', fontWeight: 'normal' }}>
                Please fix the highlighted errors in the table below before uploading.
              </div>
            )}
          </div>
        )}

        {dataArray.length > 0 && (
          <div style={{
            backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb',
            padding: '12px', marginBottom: '12px', display: 'flex',
            justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>Show:</label>
              <select value={itemsPerPage} onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                style={{ border: '1px solid #d1d5db', borderRadius: '4px', padding: '3px 6px',
                  fontSize: '11px', backgroundColor: 'white', cursor: 'pointer', width: '70px' }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>entries per page</span>
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              Showing {startIndex + 1} to {Math.min(endIndex, dataArray.length)} of {dataArray.length} entries
              {isPreviewMode && <span style={{ marginLeft: '8px', color: '#2563eb' }}>• Preview Mode</span>}
            </div>
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}
                  style={{ padding: '4px 8px', fontSize: '11px', border: '1px solid #d1d5db',
                    borderRadius: '4px', backgroundColor: currentPage === 1 ? '#f9fafb' : 'white',
                    color: currentPage === 1 ? '#9ca3af' : '#374151',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}>
                  Previous
                </button>
                {getPageNumbers(totalPages).map((pageNum, index) => (
                  <button key={index} onClick={() => typeof pageNum === 'number' && handlePageChange(pageNum)}
                    disabled={pageNum === '...'}
                    style={{ padding: '4px 8px', fontSize: '11px', border: '1px solid #d1d5db',
                      borderRadius: '4px', backgroundColor: pageNum === currentPage ? '#2563eb' : 'white',
                      color: pageNum === currentPage ? 'white' : pageNum === '...' ? '#9ca3af' : '#374151',
                      cursor: pageNum === '...' ? 'default' : 'pointer',
                      fontWeight: pageNum === currentPage ? '500' : 'normal' }}>
                    {pageNum}
                  </button>
                ))}
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}
                  style={{ padding: '4px 8px', fontSize: '11px', border: '1px solid #d1d5db',
                    borderRadius: '4px', backgroundColor: currentPage === totalPages ? '#f9fafb' : 'white',
                    color: currentPage === totalPages ? '#9ca3af' : '#374151',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}>
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <div style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
            <h2 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0 }}>
              {isPreviewMode ? `Data Preview (${dataArray.length} records)` : `Upload Results (${dataArray.length} records)`}
              {dataArray.length > 0 ? (
                <span style={{ 
                  color: isPreviewMode ? '#2563eb' : '#059669', 
                  fontSize: '12px', 
                  marginLeft: '8px' 
                }}>
                  • {isPreviewMode ? 'Review & Edit' : 'Data Loaded'}
                </span>
              ) : (
                <span style={{ color: '#6b7280', fontSize: '12px', marginLeft: '8px' }}>• No data yet</span>
              )}
            </h2>
          </div>
          
          <div style={{ overflow: 'auto', maxHeight: '60vh', border: '1px solid #e5e7eb' }}>
            <table style={{ minWidth: '100%', fontSize: '12px', color: '#374151' }}>
              <thead style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0, zIndex: 10, borderBottom: '1px solid #d1d5db' }}>
                <tr>
                  {uploadColumnOrder.map((key, index) => (
                    <th key={key}
                      draggable
                      onDragStart={(e) => handleDragStart(e, key)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, key, true)}
                      onClick={() => handleSort(key)}
                      style={{
                        padding: '8px', textAlign: 'left',
                        borderRight: index < uploadColumnOrder.length - 1 ? '1px solid #d1d5db' : 'none',
                        width: uploadColumnDefinitions[key].width, fontWeight: '600',
                        cursor: 'pointer', userSelect: 'none',
                        backgroundColor: draggedColumn === key ? '#e5e7eb' : '#f9fafb'
                      }}
                      title="Click to sort • Drag to reorder">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>{uploadColumnDefinitions[key].name}</span>
                        <span style={{ fontSize: '10px', opacity: 0.7 }}>{getSortIcon(key)}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dataArray.length === 0 ? (
                  <tr>
                    <td colSpan={uploadColumnOrder.length} style={{ 
                      padding: '40px', textAlign: 'center', color: '#6b7280',
                      fontSize: '14px', fontStyle: 'italic'
                    }}>
                      No data uploaded yet. Upload a CSV file to see data here.
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((row) => {
                    // Use the stable original index (survives sorting) so edited
                    // values, validation errors, and edit writes all line up with
                    // the correct source row.
                    const actualIndex = row.__originalIndex;
                    const rowErrors = validationErrors[actualIndex] || {};
                    const hasErrors = Object.keys(rowErrors).length > 0;

                    return (
                      <tr key={actualIndex} style={{
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: hasErrors ? '#fef2f2' : 'transparent'
                      }}>
                        {uploadColumnOrder.map((key, colIndex) => {
                          const cellValue = getCellValue(actualIndex, key, row[key]);
                          const cellError = rowErrors[key];
                          
                          return (
                            <td key={key} style={{
                              padding: '8px',
                              borderRight: colIndex < uploadColumnOrder.length - 1 ? '1px solid #e5e7eb' : 'none',
                              backgroundColor: cellError ? '#fef2f2' : 'transparent',
                              border: cellError ? '1px solid #fecaca' : 'none',
                              position: 'relative'
                            }}
                            title={cellError ? cellError : ''}>
                              {isPreviewMode ? (
                                <div style={{ position: 'relative' }}>
                                  {key === 'debt_id' && (
                                    // type="text" + inputMode="numeric" — see comment on the
                                    // currency input below for why we avoid type="number" here.
                                    <input
                                      type="text" inputMode="numeric"
                                      value={cellValue == null ? '' : cellValue}
                                      onChange={(e) => handleCellEdit(actualIndex, key, e.target.value)}
                                      style={{
                                        width: '100%', padding: '2px 4px', fontSize: '11px',
                                        border: cellError ? '2px solid #dc2626' : '1px solid #d1d5db',
                                        borderRadius: '3px', backgroundColor: cellError ? '#fef2f2' : 'white'
                                      }}
                                    />
                                  )}
                                  {key === 'has_summons' && (
                                    <select
                                      value={cellValue != null ? String(cellValue) : ''} 
                                      onChange={(e) => handleCellEdit(actualIndex, key, e.target.value)}
                                      style={{
                                        width: '100%', padding: '2px 4px', fontSize: '11px',
                                        border: cellError ? '2px solid #dc2626' : '1px solid #d1d5db',
                                        borderRadius: '3px', backgroundColor: cellError ? '#fef2f2' : 'white'
                                      }}
                                    >
                                      <option value="">Select</option>
                                      <option value="1">TRUE</option>
                                      <option value="0">FALSE</option>
                                    </select>
                                  )}
                                  {(key === 'poa_sent_date' || key === 'summons_date' || key === 'service_date' || key === 'voided_date') && (
                                    <input
                                      type="date" value={cellValue || ''}
                                      max={new Date().toISOString().split('T')[0]}
                                      onChange={(e) => handleCellEdit(actualIndex, key, e.target.value)}
                                      style={{
                                        width: '100%', padding: '2px 4px', fontSize: '11px',
                                        border: cellError ? '2px solid #dc2626' : '1px solid #d1d5db',
                                        borderRadius: '3px', backgroundColor: cellError ? '#fef2f2' : 'white'
                                      }}
                                    />
                                  )}
                                  {(key === 'void_reason' || key === 'voided_sif_id') && (
                                    <input
                                      type="text" value={cellValue || ''}
                                      onChange={(e) => handleCellEdit(actualIndex, key, e.target.value)}
                                      style={{
                                        width: '100%', padding: '2px 4px', fontSize: '11px',
                                        border: cellError ? '2px solid #dc2626' : '1px solid #d1d5db',
                                        borderRadius: '3px', backgroundColor: cellError ? '#fef2f2' : 'white'
                                      }}
                                    />
                                  )}
                                  {key === 'voided_sif_remaining_client_obligation' && (
                                    // Use type="text" + inputMode="decimal" instead of type="number".
                                    // Native number inputs silently reject any non-numeric value (e.g.
                                    // "abc", "$100", "1,000", negatives below min) — the cell appears
                                    // empty and effectively non-editable for invalid CSV values. Text
                                    // mode lets users see the original invalid value and correct it
                                    // in place. Validation still catches bad input via Number(value)
                                    // in validateRow.
                                    <input
                                      type="text" inputMode="decimal"
                                      value={cellValue == null ? '' : cellValue}
                                      onChange={(e) => handleCellEdit(actualIndex, key, e.target.value)}
                                      style={{
                                        width: '100%', padding: '2px 4px', fontSize: '11px',
                                        border: cellError ? '2px solid #dc2626' : '1px solid #d1d5db',
                                        borderRadius: '3px', backgroundColor: cellError ? '#fef2f2' : 'white'
                                      }}
                                    />
                                  )}
                                  {(key === 'legal_negotiator' || key === 'legal_resolution_status' || key === 'poa_type') && (
                                    <input
                                      type="text" value={cellValue || ''}
                                      onChange={(e) => handleCellEdit(actualIndex, key, e.target.value)}
                                      style={{
                                        width: '100%', padding: '2px 4px', fontSize: '11px',
                                        border: cellError ? '2px solid #dc2626' : '1px solid #d1d5db',
                                        borderRadius: '3px', backgroundColor: cellError ? '#fef2f2' : 'white'
                                      }}
                                    />
                                  )}
                                  {key === 'prelegal' && (
                                    <select
                                      value={cellValue != null ? String(cellValue) : ''}
                                      onChange={(e) => handleCellEdit(actualIndex, key, e.target.value)}
                                      style={{
                                        width: '100%', padding: '2px 4px', fontSize: '11px',
                                        border: cellError ? '2px solid #dc2626' : '1px solid #d1d5db',
                                        borderRadius: '3px', backgroundColor: cellError ? '#fef2f2' : 'white'
                                      }}
                                    >
                                      <option value="">Select</option>
                                      <option value="0">No</option>
                                      <option value="1">Yes</option>
                                    </select>
                                  )}
                                  {(key === 'sif_auth' || key === 'sif_letter') && (
                                    <input
                                      type="text" value={cellValue || ''}
                                      onChange={(e) => handleCellEdit(actualIndex, key, e.target.value)}
                                      style={{
                                        width: '100%', padding: '2px 4px', fontSize: '11px',
                                        border: cellError ? '2px solid #dc2626' : '1px solid #d1d5db',
                                        borderRadius: '3px', backgroundColor: cellError ? '#fef2f2' : 'white'
                                      }}
                                    />
                                  )}
                                  {key === 'notes' && (
                                    <input
                                      type="text" value={cellValue || ''}
                                      onChange={(e) => handleCellEdit(actualIndex, key, e.target.value)}
                                      style={{
                                        width: '100%', padding: '2px 4px', fontSize: '11px',
                                        border: cellError ? '2px solid #dc2626' : '1px solid #d1d5db',
                                        borderRadius: '3px', backgroundColor: cellError ? '#fef2f2' : 'white'
                                      }}
                                    />
                                  )}
                                  {cellError && (
                                    <div style={{ 
                                      position: 'absolute', bottom: '-18px', left: '0',
                                      fontSize: '10px', color: '#dc2626', whiteSpace: 'nowrap',
                                      backgroundColor: 'white', padding: '1px 3px',
                                      borderRadius: '2px', border: '1px solid #fecaca', zIndex: 5
                                    }}>
                                      {cellError}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <>
                                  {key === 'debt_id' && <span style={{ fontWeight: '500' }}>{row.debt_id}</span>}
                                  {key === 'has_summons' && (
                                    row.has_summons !== undefined && row.has_summons !== null && row.has_summons !== '' ? (
                                      <span style={{
                                        padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '500',
                                        backgroundColor: Number(row.has_summons) === 1 ? '#fef3c7' : '#f3f4f6',
                                        color: Number(row.has_summons) === 1 ? '#92400e' : '#374151'
                                      }}>
                                        {Number(row.has_summons) === 1 ? 'TRUE' : 'FALSE'}
                                      </span>
                                    ) : ''
                                  )}
                                  {(key === 'poa_sent_date' || key === 'summons_date' || key === 'service_date' || key === 'voided_date') &&
                                    (row[key] ? formatDate(row[key]) : '')}
                                  {(key === 'legal_negotiator' || key === 'legal_resolution_status' || key === 'poa_type' || key === 'void_reason' || key === 'voided_sif_id') &&
                                    (row[key] || '')}
                                  {(key === 'voided_sif_remaining_client_obligation')
                                    && (row[key] !== undefined && row[key] !== null && row[key] !== '' ? formatCurrency(row[key]) : '')}
                                  {key === 'prelegal' && (
                                    row.prelegal !== undefined && row.prelegal !== null && row.prelegal !== '' ? (
                                      <span style={{
                                        padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '500',
                                        backgroundColor: Number(row.prelegal) === 1 ? '#d1fae5' : '#f3f4f6',
                                        color: Number(row.prelegal) === 1 ? '#065f46' : '#374151'
                                      }}>
                                        {Number(row.prelegal) === 1 ? 'Yes' : 'No'}
                                      </span>
                                    ) : ''
                                  )}
                                  {(key === 'sif_auth' || key === 'sif_letter') && (row[key] || '')}
                                  {key === 'notes' && (row[key] || '')}
                                </>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Render history table with sorting and pagination
  const renderHistoryTable = () => {
    // Pick the active column registry based on which update type the user
    // is viewing. For Contact history, drag-to-reorder is intentionally
    // disabled — the column order is a const for MVP. Lift to useState +
    // extend handleDrop with a third branch if you want it draggable.
    const isContactHistory = historyType === 'contact';
    const columnOrder       = isContactHistory ? contactHistoryColumnOrder       : historyColumnOrder;
    const columnDefinitions = isContactHistory ? contactHistoryColumnDefinitions : historyColumnDefinitions;

    let dataArray = historyData ? [...historyData] : [];

    dataArray = dataArray.filter(record => {
      const resultMatch = resultFilter === 'All' ||
        (resultFilter === 'Success' && record.success) ||
        (resultFilter === 'Failed' && !record.success);
      return resultMatch;
    });

    if (sortConfig.key && dataArray.length > 0) {
      dataArray = dataArray.sort((a, b) => {
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';

        const numericColumns = isContactHistory
          ? ['loan_funded_amount']
          : ['debt_id', 'voided_sif_remaining_client_obligation'];
        if (numericColumns.includes(sortConfig.key)) {
          const aNum = parseFloat(aValue) || 0;
          const bNum = parseFloat(bValue) || 0;
          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }

        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();

        if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    const totalPages = Math.ceil(dataArray.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = dataArray.slice(startIndex, endIndex);

    return (
      <div>
        {dataArray.length > 0 && (
          <div style={{
            backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb',
            padding: '12px', marginBottom: '12px', display: 'flex',
            justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>Show:</label>
              <select value={itemsPerPage} onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                style={{ border: '1px solid #d1d5db', borderRadius: '4px', padding: '3px 6px',
                  fontSize: '11px', backgroundColor: 'white', cursor: 'pointer', width: '70px' }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>entries per page</span>
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              Showing {startIndex + 1} to {Math.min(endIndex, dataArray.length)} of {dataArray.length} entries
            </div>
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}
                  style={{ padding: '4px 8px', fontSize: '11px', border: '1px solid #d1d5db',
                    borderRadius: '4px', backgroundColor: currentPage === 1 ? '#f9fafb' : 'white',
                    color: currentPage === 1 ? '#9ca3af' : '#374151',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}>
                  Previous
                </button>
                {getPageNumbers(totalPages).map((pageNum, index) => (
                  <button key={index} onClick={() => typeof pageNum === 'number' && handlePageChange(pageNum)}
                    disabled={pageNum === '...'}
                    style={{ padding: '4px 8px', fontSize: '11px', border: '1px solid #d1d5db',
                      borderRadius: '4px', backgroundColor: pageNum === currentPage ? '#2563eb' : 'white',
                      color: pageNum === currentPage ? 'white' : pageNum === '...' ? '#9ca3af' : '#374151',
                      cursor: pageNum === '...' ? 'default' : 'pointer',
                      fontWeight: pageNum === currentPage ? '500' : 'normal' }}>
                    {pageNum}
                  </button>
                ))}
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}
                  style={{ padding: '4px 8px', fontSize: '11px', border: '1px solid #d1d5db',
                    borderRadius: '4px', backgroundColor: currentPage === totalPages ? '#f9fafb' : 'white',
                    color: currentPage === totalPages ? '#9ca3af' : '#374151',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}>
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <div style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
            <h2 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0 }}>
              {isContactHistory ? 'Contact Level' : 'Debt Level'} Historical Logs ({dataArray.length} records)
              {historyData && historyData.length > 0 ? (
                <span style={{ color: '#059669', fontSize: '12px', marginLeft: '8px' }}>• Data Loaded</span>
              ) : (
                <span style={{ color: '#6b7280', fontSize: '12px', marginLeft: '8px' }}>• No data yet</span>
              )}
            </h2>
          </div>
          
          <div style={{ overflow: 'auto', maxHeight: '60vh', border: '1px solid #e5e7eb' }}>
            <table style={{ minWidth: '100%', fontSize: '12px', color: '#374151' }}>
              <thead style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0, zIndex: 10, borderBottom: '1px solid #d1d5db' }}>
                <tr>
                  {columnOrder.map((key, index) => (
                    <th key={key}
                      draggable={!isContactHistory}
                      onDragStart={isContactHistory ? undefined : (e) => handleDragStart(e, key)}
                      onDragOver={isContactHistory ? undefined : handleDragOver}
                      onDrop={isContactHistory ? undefined : (e) => handleDrop(e, key, false)}
                      onClick={() => handleSort(key)}
                      style={{
                        padding: '8px', textAlign: 'left',
                        borderRight: index < columnOrder.length - 1 ? '1px solid #d1d5db' : 'none',
                        width: columnDefinitions[key].width, fontWeight: '600',
                        cursor: 'pointer', userSelect: 'none',
                        backgroundColor: draggedColumn === key ? '#e5e7eb' : '#f9fafb'
                      }}
                      title={isContactHistory ? 'Click to sort' : 'Click to sort • Drag to reorder'}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>{columnDefinitions[key].name}</span>
                        <span style={{ fontSize: '10px', opacity: 0.7 }}>{getSortIcon(key)}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dataArray.length === 0 ? (
                  <tr>
                    <td colSpan={columnOrder.length} style={{
                      padding: '40px', textAlign: 'center', color: '#6b7280',
                      fontSize: '14px', fontStyle: 'italic'
                    }}>
                      No historical data available. Use filters above and click "Get History" to retrieve data.
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((record, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      {columnOrder.map((key, colIndex) => (
                        <td key={key} style={{
                          padding: '8px',
                          borderRight: colIndex < columnOrder.length - 1 ? '1px solid #e5e7eb' : 'none'
                        }}>
                          {/* Debt-level identity */}
                          {key === 'debt_id' && <span style={{ fontWeight: '500' }}>{record.debt_id}</span>}
                          {/* Contact-level identity + fields */}
                          {key === 'contact_id' && <span style={{ fontWeight: '500' }}>{record.contact_id}</span>}
                          {(key === 'loan_funded_date' || key === 'last_contact_date'
                            || key === 'last_check_in_attempt' || key === 'last_check_in_completed') &&
                            (record[key] ? formatDate(record[key]) : '')}
                          {key === 'loan_funded_amount' &&
                            (record[key] !== undefined && record[key] !== null && record[key] !== ''
                              ? formatCurrency(record[key]) : '')}
                          {(key === 'loan_partner' || key === 'loan_outreach_status' || key === 'pdr_loan_status') &&
                            (record[key] || '')}
                          {key === 'has_summons' && (
                            record.has_summons !== undefined && record.has_summons !== null && record.has_summons !== '' ? (
                              <span style={{
                                padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '500',
                                backgroundColor: Number(record.has_summons) === 1 ? '#fef3c7' : '#f3f4f6',
                                color: Number(record.has_summons) === 1 ? '#92400e' : '#374151'
                              }}>
                                {Number(record.has_summons) === 1 ? 'TRUE' : 'FALSE'}
                              </span>
                            ) : ''
                          )}
                          {(key === 'poa_sent_date' || key === 'summons_date' || key === 'service_date' || key === 'voided_date' || key === 'date') &&
                            (record[key] ? formatDate(record[key]) : '')}
                          {(key === 'void_reason' || key === 'voided_sif_id') && (record[key] || '')}
                          {key === 'voided_sif_remaining_client_obligation' &&
                            (record[key] !== undefined && record[key] !== null && record[key] !== '' ? formatCurrency(record[key]) : '')}
                          {key === 'legal_negotiator' && record.legal_negotiator}
                          {key === 'legal_resolution_status' && (
                            <span style={{
                              padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '500',
                              backgroundColor: record.legal_resolution_status === 'Resolved' ? '#dcfce7' : 
                                              record.legal_resolution_status === 'Pending' ? '#fef3c7' : '#f3f4f6',
                              color: record.legal_resolution_status === 'Resolved' ? '#166534' : 
                                     record.legal_resolution_status === 'Pending' ? '#92400e' : '#374151'
                            }}>
                              {record.legal_resolution_status || 'Unknown'}
                            </span>
                          )}
                          {(key === 'poa_type' || key === 'time') && (record[key] || '')}
                          {key === 'prelegal' && (
                            record.prelegal !== undefined && record.prelegal !== null && record.prelegal !== '' ? (
                              <span style={{
                                padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '500',
                                backgroundColor: Number(record.prelegal) === 1 ? '#d1fae5' : '#f3f4f6',
                                color: Number(record.prelegal) === 1 ? '#065f46' : '#374151'
                              }}>
                                {Number(record.prelegal) === 1 ? 'Yes' : 'No'}
                              </span>
                            ) : ''
                          )}
                          {key === 'success' && (
                            <span style={{
                              padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '500',
                              backgroundColor: record.success ? '#dcfce7' : '#fef2f2',
                              color: record.success ? '#166534' : '#dc2626'
                            }}>
                              {record.success ? 'Success' : 'Failed'}
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const selectStyle = {
    border: '1px solid #d1d5db', borderRadius: '4px', padding: '6px 8px',
    fontSize: '11px', backgroundColor: 'white', cursor: 'pointer'
  };

   return (
    <StyledContainer>
      <div className="app-container">
        <div className="container">

          <h1>Bulk Update Tool</h1>
          <Breadcrumbs />

          <div style={{ minHeight: '50vh', padding: '1em 0', borderRadius: '20px' }}>
            <div style={{ width: '100%', margin: '0 auto' }}>
          
              {/* Tab Navigation */}
              <div style={{
                backgroundColor: 'white', borderRadius: '8px 8px 0 0', border: '1px solid #e5e7eb',
                borderBottom: 'none', display: 'flex'
              }}>
                <button
                  onClick={() => setActiveTab('upload')}
                  style={{
                    padding: '12px 20px', fontSize: '12px', fontWeight: '500', border: 'none',
                    borderRight: '1px solid #e5e7eb', borderRadius: '8px 0 0 0',
                    backgroundColor: activeTab === 'upload' ? '#2563eb' : '#f9fafb',
                    color: activeTab === 'upload' ? 'white' : '#374151', cursor: 'pointer'
                  }}
                >
                  Debt Level Update
                </button>
                <button
                  onClick={() => setActiveTab('contact')}
                  style={{
                    padding: '12px 20px', fontSize: '12px', fontWeight: '500', border: 'none',
                    borderRight: '1px solid #e5e7eb',
                    backgroundColor: activeTab === 'contact' ? '#2563eb' : '#f9fafb',
                    color: activeTab === 'contact' ? 'white' : '#374151', cursor: 'pointer'
                  }}
                >
                  Contact Level Update
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  style={{
                    padding: '12px 20px', fontSize: '12px', fontWeight: '500', border: 'none',
                    borderRadius: '0 8px 0 0',
                    backgroundColor: activeTab === 'history' ? '#2563eb' : '#f9fafb',
                    color: activeTab === 'history' ? 'white' : '#374151', cursor: 'pointer'
                  }}
                >
                  Historical Logs
                </button>
              </div>

              {/* Content Area */}
              <div style={{
                backgroundColor: 'white', border: '1px solid #e5e7eb',
                borderRadius: '0 0 8px 8px', padding: '20px'
              }}>
                
                {/* Upload Tab Content */}
                {activeTab === 'upload' && (
                  <div>
                    {/* Upload Controls */}
                    <div style={{ 
                      backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', 
                      padding: '16px', marginBottom: '20px', display: 'flex', gap: '12px',
                      flexWrap: 'wrap', alignItems: 'end'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: '200px' }}>
                        <label style={{ fontSize: '11px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                          Choose File
                        </label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <input
                            type="file"
                            accept=".csv"
                            onChange={(e) => setFile(e.target.files[0])}
                            required
                            disabled={isPreviewMode}
                            style={{
                              ...selectStyle,
                              width: '200px',
                              opacity: isPreviewMode ? 0.6 : 1,
                              cursor: isPreviewMode ? 'not-allowed' : 'pointer'
                            }}
                          />
                          {file && (
                            <span style={{ fontSize: '10px', color: '#059669', fontWeight: '500' }}>
                              ✓ {file.name}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                        {!isPreviewMode ? (
                          <button
                            onClick={handleFileUpload}
                            disabled={isLoading || !file}
                            style={{
                              backgroundColor: (isLoading || !file) ? '#9ca3af' : '#2563eb',
                              color: 'white', padding: '8px 16px', borderRadius: '4px',
                              fontSize: '11px', border: 'none', fontWeight: '500',
                              cursor: (isLoading || !file) ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {isLoading ? 'Loading...' : 'Preview CSV'}
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={handleCancelPreview}
                              disabled={isLoading}
                              style={{
                                backgroundColor: '#6b7280', color: 'white', padding: '8px 16px',
                                borderRadius: '4px', fontSize: '11px', border: 'none', fontWeight: '500',
                                cursor: isLoading ? 'not-allowed' : 'pointer'
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleConfirmUpload}
                              disabled={isLoading || !isAllRowsValid()}
                              style={{
                                backgroundColor: (isLoading || !isAllRowsValid()) ? '#9ca3af' : '#059669',
                                color: 'white', padding: '8px 16px', borderRadius: '4px',
                                fontSize: '11px', border: 'none', fontWeight: '500',
                                cursor: (isLoading || !isAllRowsValid()) ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {isLoading ? 'Uploading...' : 'Confirm Upload'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Status Messages */}
                    {status.show && (
                      <div style={{
                        padding: '12px', marginBottom: '20px', borderRadius: '8px', fontSize: '12px',
                        backgroundColor: status.isSuccess ? '#dcfce7' : '#fef2f2',
                        color: status.isSuccess ? '#166534' : '#dc2626',
                        border: `1px solid ${status.isSuccess ? '#bbf7d0' : '#fecaca'}`
                      }}>
                        {status.message}
                      </div>
                    )}

                    {renderUploadTable()}
                  </div>
                )}

                {/* History Tab Content */}
                {activeTab === 'history' && (
                  <div>
                    {/* Filter Controls */}
                    <div style={{
                      backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb',
                      padding: '16px', marginBottom: '20px', display: 'flex', gap: '12px',
                      flexWrap: 'wrap', alignItems: 'end'
                    }}>
                      {/* Update Type selector — drives which Pipedream endpoint
                          fetchHistory calls AND which column set renderHistoryTable
                          uses. Flipping clears any stale data (see the useEffect
                          tied to historyType). */}
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: '180px' }}>
                        <label style={{ fontSize: '11px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                          Update Type
                        </label>
                        <select
                          value={historyType}
                          onChange={(e) => setHistoryType(e.target.value)}
                          style={selectStyle}
                        >
                          <option value="debt">Debt Level Update</option>
                          <option value="contact">Contact Level Update</option>
                        </select>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: '140px' }}>
                        <label style={{ fontSize: '11px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                          Start Date
                        </label>
                        <input
                          type="date" value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          style={selectStyle}
                        />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: '140px' }}>
                        <label style={{ fontSize: '11px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                          End Date
                        </label>
                        <input
                          type="date" value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          style={selectStyle}
                        />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: '140px' }}>
                        <label style={{ fontSize: '11px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                          Result
                        </label>
                        <select
                          value={resultFilter}
                          onChange={(e) => setResultFilter(e.target.value)}
                          style={selectStyle}
                        >
                          <option value="All">All</option>
                          <option value="Success">Success</option>
                          <option value="Failed">Failed</option>
                        </select>
                      </div>

                      <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                        <button
                          onClick={fetchHistory}
                          disabled={isLoading || !startDate || !endDate}
                          style={{
                            backgroundColor: (isLoading || !startDate || !endDate) ? '#9ca3af' : '#2563eb',
                            color: 'white', padding: '8px 16px', borderRadius: '4px',
                            fontSize: '11px', border: 'none', fontWeight: '500',
                            cursor: (isLoading || !startDate || !endDate) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {isLoading ? 'Loading...' : 'Get History'}
                        </button>
                      </div>
                    </div>

                    {/* History Status */}
                    {historyStatus.show && (
                      <div style={{
                        padding: '12px', marginBottom: '20px', borderRadius: '8px', fontSize: '12px',
                        backgroundColor: historyStatus.isSuccess ? '#dcfce7' : '#fef2f2',
                        color: historyStatus.isSuccess ? '#166534' : '#dc2626',
                        border: `1px solid ${historyStatus.isSuccess ? '#bbf7d0' : '#fecaca'}`
                      }}>
                        {historyStatus.message}
                      </div>
                    )}

                    {renderHistoryTable()}
                  </div>
                )}

                {/* Contact Update Tab Content */}
                {activeTab === 'contact' && (
                  <div>
                    {/* Upload Controls — mirrors the Upload CSV tab's controls
                        but binds to contactFile / contactIsPreviewMode and
                        delegates to handleContactFileUpload / handleContactConfirmUpload. */}
                    <div style={{
                      backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb',
                      padding: '16px', marginBottom: '20px', display: 'flex', gap: '12px',
                      flexWrap: 'wrap', alignItems: 'end'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: '200px' }}>
                        <label style={{ fontSize: '11px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                          Choose File
                        </label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <input
                            type="file"
                            accept=".csv"
                            onChange={(e) => setContactFile(e.target.files[0])}
                            disabled={contactIsPreviewMode}
                            style={{
                              ...selectStyle, width: '200px',
                              opacity: contactIsPreviewMode ? 0.6 : 1,
                              cursor: contactIsPreviewMode ? 'not-allowed' : 'pointer',
                            }}
                          />
                          {contactFile && (
                            <span style={{ fontSize: '10px', color: '#059669', fontWeight: '500' }}>
                              ✓ {contactFile.name}
                            </span>
                          )}
                        </div>
                      </div>

                      <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                        {!contactIsPreviewMode ? (
                          <button
                            onClick={handleContactFileUpload}
                            disabled={isLoading || !contactFile}
                            style={{
                              backgroundColor: (isLoading || !contactFile) ? '#9ca3af' : '#2563eb',
                              color: 'white', padding: '8px 16px', borderRadius: '4px',
                              fontSize: '11px', border: 'none', fontWeight: '500',
                              cursor: (isLoading || !contactFile) ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {isLoading ? 'Loading...' : 'Preview CSV'}
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={handleContactCancelPreview}
                              disabled={isLoading}
                              style={{
                                backgroundColor: '#6b7280', color: 'white', padding: '8px 16px',
                                borderRadius: '4px', fontSize: '11px', border: 'none',
                                fontWeight: '500', cursor: isLoading ? 'not-allowed' : 'pointer',
                              }}
                            >Cancel</button>
                            <button
                              onClick={handleContactConfirmUpload}
                              disabled={isLoading || !isAllContactRowsValid()}
                              style={{
                                backgroundColor: (isLoading || !isAllContactRowsValid()) ? '#9ca3af' : '#059669',
                                color: 'white', padding: '8px 16px', borderRadius: '4px',
                                fontSize: '11px', border: 'none', fontWeight: '500',
                                cursor: (isLoading || !isAllContactRowsValid()) ? 'not-allowed' : 'pointer',
                              }}
                            >
                              {isLoading ? 'Uploading...' : 'Confirm Upload'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Status Messages */}
                    {contactStatus.show && (
                      <div style={{
                        padding: '12px', marginBottom: '20px', borderRadius: '8px', fontSize: '12px',
                        backgroundColor: contactStatus.isSuccess ? '#dcfce7' : '#fef2f2',
                        color: contactStatus.isSuccess ? '#166534' : '#dc2626',
                        border: `1px solid ${contactStatus.isSuccess ? '#bbf7d0' : '#fecaca'}`,
                      }}>
                        {contactStatus.message}
                      </div>
                    )}

                    {renderContactUploadTable()}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{
                backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb',
                padding: '12px', marginTop: '12px', textAlign: 'center',
                fontSize: '12px', color: '#6b7280'
              }}>
                <div style={{ marginBottom: '4px', fontSize: '11px' }}>
                  <strong>Tip:</strong> Click column headers to sort data. Drag headers to reorder columns. Use pagination controls to navigate through all records.
                </div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>
                  <strong>Required Fields:</strong> Debt ID | <strong>Date Format:</strong> DD/MM/YYYY
                </div>
                {isPreviewMode && (
                  <div style={{ fontSize: '11px', color: '#2563eb', marginTop: '4px' }}>
                    <strong>Preview Mode:</strong> Review and edit data above. Fix highlighted errors before confirming upload.
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className='custom-footer'>
            <Footer/>
          </div>
        </div>
      </div>
    </StyledContainer>
  );
};

export default BulkUpdateTool;

const StyledContainer = styled.div`
  .spanText {
    padding-right: 10px;
    font-size: 20px;
  }
`;